/**
 * Skeleton — block placeholder with a slow shimmer.
 * Sharp corners (no rounding). Use as a single block or compose multiple to
 * mirror the shape of the content that's loading.
 *
 * `<Skeleton.Row gridTemplate="…" cells={n} />` matches a DataTable row's grid
 * so loading states preserve column rhythm.
 */

interface SkeletonProps {
  /** CSS width. Default `100%`. */
  width?: string | number;
  /** CSS height. Default `1em`. */
  height?: string | number;
  className?: string;
}

export function Skeleton({ width = '100%', height = '1em', className = '' }: SkeletonProps) {
  return (
    <span
      aria-hidden
      className={'block animate-pulse bg-surface-2 ' + className}
      style={{ width, height }}
    />
  );
}

interface SkeletonRowProps {
  /** CSS grid-template-columns track string. */
  gridTemplate: string;
  /** Number of skeleton cells to render. Should match column count. */
  cells: number;
  /** Extra classes on the row element. */
  className?: string;
}

function SkeletonRow({ gridTemplate, cells, className = '' }: SkeletonRowProps) {
  return (
    <div
      role="row"
      aria-busy
      className={'grid border-b border-line py-3 ' + className}
      style={{ gridTemplateColumns: gridTemplate }}
    >
      {Array.from({ length: cells }).map((_, i) => (
        <div key={i} className="px-2.5">
          <Skeleton height={12} width={`${50 + ((i * 17) % 40)}%`} />
        </div>
      ))}
    </div>
  );
}

Skeleton.Row = SkeletonRow;
